// Copyright (c) Docugami, Inc. All rights reserved.

import { MessageId } from "messaging/MessageTypes";
import { TextEditCommand } from "messaging/messages/TextEditMessage";
import { ChunkInstance } from "networking/api";
import { useMessagePublisher } from "./MessagePublisher";
import { useAppSelector } from "./redux";
import { useChunkSelection } from "./chunkSelection";
import { sleep } from "utils/AsyncHelpers";
import { ICommand } from "components/cards/CommandButton";
import { useMemo } from "react";

/* Hook that creates a command to trigger text edit for a given instance externally of the WebDocHost */
export function useTextEditCommand(clusterId: string, chunk?: ChunkInstance): ICommand {
    const isExternalModalActive = useAppSelector(state => state.documents.modalSelectionContext.isActive);
    const isLabelEditing = useAppSelector(state => !!state.reportsSlice.selectedLabel?.isEditing);
    const isSample = useAppSelector(state => !!state.clusters.indexed[clusterId]?.clusterInfo.isDocugamiCluster)

    const { sendCommand } = useMessagePublisher();
    const { selectChunk } = useChunkSelection();

    const isValid = !!chunk?.spanId && !chunk?.isAddedChunk;
    const canExecute = isValid && !isExternalModalActive && !isSample && !isLabelEditing;

    return useMemo(() =>  ({
        canExecute: () => canExecute,
        execute: async () => {
            if (!isValid) {
                return;
            }

            selectChunk(chunk!.spanId!);

            // Await the selection change in the sdv store
            await sleep(100);

            const command: TextEditCommand = {
                id: MessageId.TextEdit,
                instance: chunk
            }

            sendCommand(command);
        },
        hidden: !isValid
    }) as ICommand, [selectChunk, isValid, canExecute, chunk, sendCommand]);
}
